import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import { useCart } from "../context/CartContext";
import { api, formatPrice } from "../lib/api";

export default function ProductDetail() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setProduct(r.data); setActiveImg(0); }).catch(() => navigate("/tienda"));
  }, [id, navigate]);

  if (!product) {
    return <div className="pt-40 text-center text-ink-soft min-h-screen">…</div>;
  }

  const handleAdd = () => {
    addItem(product);
    toast.success(lang === "es" ? "Agregado al carrito" : "Added to cart");
  };

  return (
    <div data-testid="product-detail-page" className="pt-32 pb-24 max-w-7xl mx-auto px-6 min-h-screen">
      <Link to="/tienda" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-ink-soft hover:text-brand-primary text-sm mb-10 transition-colors">
        <ArrowLeft size={16} /> {t("back_to_shop")}
      </Link>

      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="overflow-hidden bg-canvas-soft aspect-[4/5] mb-4">
            <img src={product.images?.[activeImg]} alt={product[`name_${lang}`]} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-24 overflow-hidden border-2 transition-colors ${activeImg === i ? "border-brand-primary" : "border-transparent"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:pt-6">
          <p className="overline text-brand-primary mb-3">{t(`cat_${product.category}`)}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-ink leading-tight">{product[`name_${lang}`]}</h1>
          <p className="text-2xl text-brand-primary font-heading mt-4">{formatPrice(product.price)}</p>
          <p className="text-ink-soft leading-relaxed mt-6">{product[`description_${lang}`]}</p>

          {product[`materials_${lang}`] && (
            <div className="mt-6 pt-6 border-t border-brand-secondary/40">
              <span className="overline text-ink-soft">{t("materials")}</span>
              <p className="text-ink mt-1">{product[`materials_${lang}`]}</p>
            </div>
          )}

          <button
            data-testid="add-to-cart-btn"
            onClick={handleAdd}
            disabled={!product.in_stock}
            className="mt-10 w-full sm:w-auto bg-brand-primary text-white px-10 py-4 text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ShoppingBag size={18} /> {product.in_stock ? t("add_to_cart") : t("out_of_stock")}
          </button>
        </div>
      </div>
    </div>
  );
}
